import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import useRead from "../../hooks/useRead";
import useCreate from "../../hooks/useCreate";
import toast from "react-hot-toast";

function useBlogsList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialData = {
    page_number: searchParams.get("page_number") || 0,
    limit: searchParams.get("limit") || 10,
    search_query: searchParams.get("search_query") || "",
  };
  
  const [openAddModal, setOpenAddModal] = useState(false);
  const [isOpenEditModal, setIsOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [blogId, setBlogId] = useState("");
  const [isDeletedSuccess, setIsDeletedSuccess] = useState(false);
  const [blogStatus, setBlogStatus] = useState(false);
  const [isToggleLoading, setIsToggleLoading] = useState(false);
  const [previousStatus, setPreviousStatus] = useState(false);
  
  // Fetch blogs list
  const { data, paramsObject, setGetListParams } = useRead({
    url: "get-blog-list",
    method: "GET",
    initialData,
    onError: (err) => {
      toast.error("There was an error while loading the data", {
        position: "top-right",
        duration: 3000,
      });
    },
  });
  
  // Fetch blog status
  useRead({
    url: "get-blog-status",
    method: "GET",
    initialData: {},
    onSuccess: (data) => {
      setBlogStatus(data.blog_status);
    },
    onError: (err) => {
      toast.error("Failed to fetch blog status", {
        position: "top-right",
        duration: 3000,
      });
    },
  });

  // Update blog status
  const { setDataToServer } = useCreate({
    url: "change-blog-status",
    refreshUrl: "get-blog-status",
    onSuccess: (data) => {},
    onError: (err) => {
      // Revert to previous state on error
      setBlogStatus(previousStatus);
      setIsToggleLoading(false);
      toast.error("Failed to update blog status", {
        position: "top-right",
        duration: 3000,
      });
    },
  });

  const handleBlogStatusChange = async (event) => {
    const newStatus = event.target.checked;
    // Store the current status before changing
    setPreviousStatus(blogStatus);
    setIsToggleLoading(true);
    setBlogStatus(newStatus);
    setDataToServer.mutate({ 
      enable: newStatus 
    }, {
      onSuccess: () => {
        toast.success(`Blogs ${newStatus ? 'enabled' : 'disabled'} successfully`, {
          position: "top-right",
          duration: 3000,
        });
        setIsToggleLoading(false);
      }
    });
  };
  
  const handleSearchVal = (e) => {
    const value = e.target.value;
    const updatedParams = {
      ...paramsObject,
      search_query: value,
    };
    setSearchParams(updatedParams);
    setGetListParams(updatedParams);
  };
  
  const handleOpenAddModal = () => {
    setOpenAddModal(true);
  };
  
  const handleModalClose = () => {
    setOpenAddModal(false);
    setIsOpenEditModal(false);
    setOpenDeleteModal(false);
  };
  
  const handleOpenEditModal = (blog_id) => {
    setBlogId(blog_id);
    setIsOpenEditModal(true);
  };
  
  const handleOpenDeleteModal = (blog_id) => {
    setOpenDeleteModal(true);
    setBlogId(blog_id);
  };
  
  const handleDeleteSuccess = () => {
    handleModalClose();
    setIsDeletedSuccess(true);
  };
  
  const handleCloseDeleteSuccessAlert = () => {
    setIsDeletedSuccess(false);
  };
  
  const handlePageClick = (val) => {
    const pageParams = {
      ...paramsObject,
      page_number: val.selected,
    };
    setGetListParams(pageParams);
    setSearchParams(pageParams);
  };

  return {
    data,
    paramsObject,
    blogId,
    openAddModal,
    isOpenEditModal,
    openDeleteModal,
    isDeletedSuccess,
    blogStatus,
    isToggleLoading,
    handleBlogStatusChange,
    handleSearchVal,
    handleOpenAddModal,
    handleModalClose,
    handleOpenEditModal,
    handleOpenDeleteModal,
    handleDeleteSuccess,
    handleCloseDeleteSuccessAlert,
    handlePageClick,
  };
}

export default useBlogsList;
